<template>
  <div class="mobile-overlay-content-wrapper" @click.stop>
    <div class="controller-section">
      <div class="caption bg-light p-2 rounded-pill">
        {{ localCurrentIndex + 1 }}/{{ localTotalImages }}
      </div>
      <button @click="toggleFullScreen" class="close-btn">
        <svg width="21" height="21" class="icon">
          <use xlink:href="#___icon___close-x"></use>
        </svg>
      </button>
    </div>
      <div class="imgs-wrapper" ref="carousel" @scroll="updateIndex">
        <div
          v-for="(image, index) in allPrestationsImages"
          :key="`${index}-${image.section}`"
          class="img-container"
        >
          <img
            :src="getImageUrl(image)"
            alt="Miniature of Accommodation"
            class="prestation-image rounded"
          />
          <div class="img-section-name">{{ image.section }}</div>
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Vue
import { nextTick, ref, watch } from "vue";
// Types
import { IPicturesAccomodationExtended } from "@/types/index";

// Props
const props = defineProps<{
  allPrestationsImages: IPicturesAccomodationExtended[];
  currentIndex: number;
  totalImages: number;
  isFullScreen: boolean;
}>();

// Emits
const emit = defineEmits(["toggleFullScreen", "update:currentIndex"]);

// Data
const localCurrentIndex = ref(props.currentIndex);
const localTotalImages = ref(props.totalImages);

// Refs
const carousel = ref<HTMLElement | null>(null);

// Methods
const toggleFullScreen = () => {
  emit("toggleFullScreen");
};

const getImageUrl = (picture: IPicturesAccomodationExtended) => {
  if (!picture) return "";
  return picture.imgXL || picture.imgLarge || picture.img;
};

const updateIndex = () => {
  if (!carousel.value) return;

  const scrollPosition = carousel.value.scrollLeft;
  const itemWidth = carousel.value.scrollWidth / props.allPrestationsImages.length;
  const newIndex = Math.round(scrollPosition / itemWidth);

  if (newIndex !== localCurrentIndex.value) {
    localCurrentIndex.value = newIndex;
    emit("update:currentIndex", newIndex);
  }
};

/**
 * Scrolls the carousel to the specified index.
 * @param {number} index - The index to scroll to.
 */
const handleScroll = (index:number) => {
  if (carousel.value) {
    const itemWidth = carousel.value.scrollWidth / props.allPrestationsImages.length;
    carousel.value.scrollTo({
      left: index * itemWidth,
      behavior: "auto",
    });
  }
}

// Watchers

/**
 * Watch for changes in the `isFullScreen`. 
 * That is needed to scroll the gallery to the correct image based on the index for the first time the scrollable gallery is open in the Overlay for Mobile.
 * `nextTick` is needed to await for all the images to be in the gallery, otherwise we will not have the correct for `carousel.value.scrollWidth`
 */
watch(
  () => props.isFullScreen,
  () => {
    nextTick(() => {
      if (props.isFullScreen) {
        handleScroll(localCurrentIndex.value)
      }
    })
  }
);

/**
 * Watch for changes in the `currentIndex`. 
 * Used to scroll the gallery to the correct image based on the index
 */
watch(
  () => props.currentIndex,
  (newVal) => {
    localCurrentIndex.value = newVal;
    handleScroll(newVal)
  }
);

watch(
  () => props.totalImages,
  (newVal) => {
    localTotalImages.value = newVal;
  }
);
</script>

<style scoped>
.mobile-overlay-content-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 95vw;
}

.controller-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f1f3f5;
}
.imgs-wrapper {
  overflow-x: scroll;
  white-space: nowrap;
  width: 100%;
  max-width: 100vw;
  scrollbar-width: none; /* For Firefox */
  display: flex;
}

.imgs-wrapper::-webkit-scrollbar {
  display: none; /* For Chrome, Safari, and Opera */
}

.img-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-right: 15px;
  flex-shrink: 0;
  width: 90vw;
  margin: 1vw;
}

.prestation-image {
  max-width: 100%;
  border-radius: var(--border-radius-card);
}

.img-section-name {
  display: block;
  margin-top: 15px;
  color: white;
  text-wrap: balance;
}

.caption {
  font-size: 14px;
}
</style>